import React, { useEffect } from "react";
import { Container, Row, Col, Card, Badge } from "react-bootstrap";

import withRouter from "Common/withRouter";

import userDummyImg from "assets/images/users/32/user-dummy-img.jpg";

// actions
import { useProfile } from "Common/Hooks/UserHooks";

// Import custom styles
import "./user-profile.css";
import { useHandleBack } from "helpers/functions/useHandleBack";
import { useNavigate } from "react-router-dom";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import { useDispatch } from "react-redux";

const UserProfile = () => {
  document.title = "User Profile";
  const handleBack = useHandleBack("/");
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const { userProfile } = useProfile();
  console.log(userProfile);

  //*// Fetch Permissions to reload the user
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  // Get user data from localStorage as fallback
  const getStoredUserData = () => {
    try {
      const storedAuthUser = localStorage.getItem("authUser");
      if (storedAuthUser) {
        const obj = JSON.parse(storedAuthUser);
        return obj?.data || obj?.user || null;
      }
    } catch (error) {
      console.error("Error parsing stored user data:", error);
    }
    return null;
  };

  // Use userProfile from hook or fallback to localStorage
  const profileData = userProfile || { user: getStoredUserData() };

  // Check if user data is available
  const isUserDataAvailable =
    profileData?.user && Object.keys(profileData.user).length > 0;

  // Helper function to get user type badge color
  const getUserTypeBadgeColor = (userType: string) => {
    switch (userType?.toLowerCase()) {
      case "admin":
        return "danger";
      case "staff":
        return "info";
      default:
        return "secondary";
    }
  };

  // Show loading or error state if no user data
  if (!isUserDataAvailable) {
    return (
      <div className="page-content bg-light min-vh-100">
        <Container fluid className="p-3 p-lg-4 bg-white shadow-sm rounded mx-2">
          {/* Header */}
          <Row className="align-items-center g-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
              {/* Left: Back icon and heading */}
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{ fontSize: "24px", cursor: "pointer" }}
                  onClick={handleBack}
                >
                  ←
                </span>
                <h5 className="mb-0 fw-bold">{"User Profile"}</h5>
              </div>

              {/* Right: Breadcrumb */}
              <div className="text-muted">
                <span
                  onClick={() => navigate("/")}
                  style={{ color: "#6c757d", cursor: "pointer" }}
                >
                  Dashboard
                </span>{" "}
                &gt;{" "}
                <span style={{ color: "#6c757d", cursor: "default" }}>
                  {"User Profile"}
                </span>
              </div>
            </div>
          </Row>

          {/* No Data State */}
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-5 text-center">
                  <div className="mb-4">
                    <i
                      className="ri-user-line"
                      style={{ fontSize: "4rem", color: "#6c757d" }}
                    ></i>
                  </div>
                  <h5 className="text-muted mb-3">No User Data Available</h5>
                  <p className="text-muted mb-4">
                    Unable to load user profile information. Please try
                    refreshing the page or contact support.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => window.location.reload()}
                  >
                    <i className="ri-refresh-line me-2"></i>
                    Refresh Page
                  </button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content bg-light min-vh-100">
      <Container fluid className="p-3 p-lg-4 bg-white shadow-sm rounded mx-2">
        {/* Header */}
        <Row className="align-items-center g-2">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
            {/* Left: Back icon and heading */}
            <div className="d-flex align-items-center gap-2">
              <span
                style={{ fontSize: "24px", cursor: "pointer" }}
                onClick={handleBack}
              >
                ←
              </span>
              <h5 className="mb-0 fw-bold">{"User Profile"}</h5>
            </div>

            {/* Right: Breadcrumb */}
            <div className="text-muted">
              <span
                onClick={() => navigate("/")}
                style={{ color: "#6c757d", cursor: "pointer" }}
              >
                Dashboard
              </span>{" "}
              &gt;{" "}
              <span style={{ color: "#6c757d", cursor: "default" }}>
                {"User Profile"}
              </span>
            </div>
          </div>
        </Row>

        {/* User Information Header */}
        <Row className="mb-3">
          <Col lg="12">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <img
                      src={userDummyImg}
                      alt="User Avatar"
                      className="rounded-circle border border-2 border-primary"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <h4 className="mb-0 me-3 fw-bold">
                        {profileData?.user?.first_name}{" "}
                        {profileData?.user?.last_name}
                      </h4>
                      <Badge
                        bg={getUserTypeBadgeColor(profileData?.user?.user_type)}
                        className="fs-6 px-3 py-1"
                      >
                        {profileData?.user?.user_type}
                      </Badge>
                    </div>
                    <p className="text-muted mb-1 fs-6">
                      <i className="ri-mail-line me-2 text-primary"></i>
                      {profileData?.user?.email}
                    </p>
                    <p className="text-muted mb-0 fs-6">
                      <i className="ri-user-line me-2 text-primary"></i>@
                      {profileData?.user?.username}
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* User Details Section */}
        <Row>
          <Col lg="12">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <div className="mb-3">
                  <h5 className="mb-2 fw-bold">
                    <i className="ri-user-settings-line me-2 text-primary"></i>
                    Personal Information
                  </h5>
                  <p className="text-muted mb-0 small">
                    Complete user profile information and account details.
                  </p>
                </div>

                <Row className="g-3">
                  <Col lg="6">
                    <Card className="border-0 bg-light h-100">
                      <Card.Body className="p-3">
                        <h6 className="text-primary mb-3 fw-bold">
                          <i className="ri-user-line me-2"></i>
                          Basic Information
                        </h6>
                        <div className="row g-2">
                          <div className="col-6">
                            <label className="form-label text-muted small fw-semibold">
                              First Name
                            </label>
                            <p className="fw-bold mb-2 text-dark">
                              {profileData?.user?.first_name}
                            </p>
                          </div>
                          <div className="col-6">
                            <label className="form-label text-muted small fw-semibold">
                              Last Name
                            </label>
                            <p className="fw-bold mb-2 text-dark">
                              {profileData?.user?.last_name}
                            </p>
                          </div>
                          <div className="col-6">
                            <label className="form-label text-muted small fw-semibold">
                              Username
                            </label>
                            <p className="fw-bold mb-2 text-dark">
                              @{profileData?.user?.username}
                            </p>
                          </div>
                          <div className="col-6">
                            <label className="form-label text-muted small fw-semibold">
                              User Type
                            </label>
                            <div>
                              <Badge
                                bg={getUserTypeBadgeColor(
                                  profileData?.user?.user_type
                                )}
                                className="fs-6 px-3 py-1"
                              >
                                {profileData?.user?.user_type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg="6">
                    <Card className="border-0 bg-light h-100">
                      <Card.Body className="p-3">
                        <h6 className="text-primary mb-3 fw-bold">
                          <i className="ri-mail-line me-2"></i>
                          Contact Information
                        </h6>
                        <div className="row g-2">
                          <div className="col-12">
                            <label className="form-label text-muted small fw-semibold">
                              Email Address
                            </label>
                            <p className="fw-bold mb-2 text-dark">
                              <i className="ri-mail-line me-2 text-primary"></i>
                              {profileData?.user?.email}
                            </p>
                          </div>
                          <div className="col-12">
                            <label className="form-label text-muted small fw-semibold">
                              Account Status
                            </label>
                            <div>
                              <Badge bg="success" className="fs-6 px-3 py-1">
                                <i className="ri-check-line me-1"></i>
                                Active
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withRouter(UserProfile);
